/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */
import TaskIcon from '@/components/TaskIcon';
import TaskTitle from '@/components/TaskTitle/TaskTitle';
import { useAppSelector } from '@/hooks';
import { useTranslations } from '@/hooks/useTranslations';
import { actions } from '@/store/Products';
import axiosInstance from '@/utils/axiosInstance';
import * as storageUtils from '@/utils/storageUtils';
import { getImagePathFromStep } from '@/utils/utils';
import { CheckBox, FlexBox, Input, InputDomRef, Option, RadioButton, Select, Ui5CustomEvent, Text } from '@ui5/webcomponents-react';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ThemingParameters } from '@ui5/webcomponents-react-base';

interface ControlTypeOption {
    id: string;
    controlTypeId: string;
    value: string;
    displayOrder: number;
    translationValueKey?: string;
}

interface ControlType {
    id: string;
    name: string;
    controlTypeOptions: ControlTypeOption[];
}

interface Content {
    id: string;
    title: string;
    iconLink: string;
    controlType: ControlType;
    step: {
        name: string;
    };
    defaultValue: string;
    personalizationConfiguration: {
        configurationKey: string;
        configurationValue: any;
    };
    productConfigurationKey: string;
}

const SourcingSkuList = [8007883, 8015622, 8000641];

const DbLayout: React.FC<{ content: Content }> = ({ content }) => {
    const personalizationValue = content?.personalizationConfiguration?.configurationValue ?? null;
    const [cardData, setCardData] = useState<any>();
    const [options, setOptions] = useState<{ key: string; value: string }[]>([]);
    const [value, setValue] = useState<any>();
    const [multiSelectValues, setMultiSelectValues] = useState<{ values: string[]; others: string }>({ values: [], others: '' });
    const [otherInputValue, setOtherInputValue] = useState<string>('');
    const dispatch = useDispatch();
    const productList = useAppSelector(state => state.productList);
    const USER_ID = storageUtils.getItem('user').user_uuid;
    const imagePath = getImagePathFromStep(content.step?.name);
    const src = imagePath || content.iconLink;
    const WAVED_APPROACH_OTHER_KEY = 'Waved Approach: Other (Please specify):';
    const OTHER_KEY = 'Other (Please specify):';
    const CERTIFICATE_OTHER_KEY: string = 'Certificates not listed here';
    const { t } = useTranslations();

    useEffect(() => {
        if (productList.readOnlyMode) {
            axiosInstance
                .get(`/product-configurations/`)
                .then(res => res.data)
                .then((aData: any) => {
                    const filteredData = aData.filter((oData: any) => oData.taskId === content.id);
                    if (filteredData.length > 0) {
                        setValue(filteredData[0].configurationValue);
                        if (content.defaultValue === 'Yes' || content.defaultValue === 'No') {
                            setOptions([
                                {
                                    key: 'Yes',
                                    value: t('YES'),
                                },
                                {
                                    key: 'No',
                                    value: t('NO'),
                                },
                            ]);
                        } else if (content.defaultValue === 'United Nations Standard Products and Services Code (UNSPSC)') {
                            setOptions([
                                {
                                    key: 'United Nations Standard Products and Services Code (UNSPSC)',
                                    value: t('UNSPSC'),
                                },
                                {
                                    key: 'Others',
                                    value: t('OTHERS'),
                                },
                            ]);
                        }
                        setCardData(content);
                    }
                });
        }
    }, [content.title]);

    useEffect(() => {
        if (productList?.currentSKU?.SKUId === 2) {
            const fetchData = async () => {
                try {
              const skuId = "8007883"; 
                    const response = await axiosInstance.get(`/ariba-nexus/${skuId}`);

                    if (Array.isArray(response.data)) {
                        const transformedOptions = response.data.map(item => ({
                            key: item.bundleName,
                            value: item.bundleDescription,
                        }));
                        setOptions(transformedOptions);
                    } else {
                        console.error('Expected array, got:', response.data);
                        setOptions([]);
                    }
                } catch (e) {
                    console.error('Error fetching bundles:', e);
                    setOptions([]);
                }
            };

            fetchData();
        }
    }, []);


    useEffect(() => {
        dispatch(
            actions.setCurrentProductConfigurationValue({
                productRunId: productList.currentRunId,
                taskId: content.id,
                configurationKey: content.productConfigurationKey,
                configurationValue: personalizationValue,
                createdBy: USER_ID,
                updatedBy: USER_ID,
                personalizationConfiguration: content.personalizationConfiguration,
            })
        );
        dispatch(
            actions.setCurrentProductConfigKeys({
                key: content.productConfigurationKey,
                value: personalizationValue,
            })
        );

        if (personalizationValue) {
            switch (content.personalizationConfiguration.configurationKey) {
                case '42k_kpi_pck':
                    setMultiSelectValues(JSON.parse(personalizationValue));
                    break;
                case '42k_supplier_rollout_pck':
                    if (personalizationValue.startsWith(WAVED_APPROACH_OTHER_KEY)) {
                        setValue(WAVED_APPROACH_OTHER_KEY);
                        setOtherInputValue(personalizationValue.replace(WAVED_APPROACH_OTHER_KEY, ''));
                    } else {
                        setValue(personalizationValue);
                    }
                    break;
                default:
                    if (content.controlType?.name === 'checkbox') {
                        setMultiSelectValues(JSON.parse(personalizationValue));
                    } else {
                        setValue(personalizationValue);
                    }
                    break;
            }
        } else {
            setValue(null);
            setMultiSelectValues({ values: [], others: '' });
        }

        if (productList.currentSKU.SKUId && !SourcingSkuList.includes(productList.currentSKU.SKUId)) {
            setOptionsSku();
        } else if (!content.defaultValue || content.defaultValue === 'Yes' || content.defaultValue === 'No') {
            setOptions([
                {
                    key: 'Yes',
                    value: 'YES',
                },
                {
                    key: 'No',
                    value: 'NO',
                },
            ]);
        } else if (content.defaultValue === 'S/4HANA Cloud: Procure to Pay with Ariba Network Integration (42K)') {
            setOptions([
                {
                    key: 'S/4HANA Cloud: Procure to Pay with Ariba Network Integration (42K)',
                    value: t('S4HANA_WITH_ARIBA_NETWORK_INTEGRATION_42K'),
                },
                {
                    key: 'Others',
                    value: t('OTHERS'),
                },
            ]);
        } else if (content.defaultValue === 'AN0102394857 (US Datacenter)') {
            setOptions([
                {
                    key: 'AN0102394857 (US Datacenter)',
                    value: t('AN0102394857_US'),
                },
                {
                    key: 'AN01023388211 (US Datacenter)',
                    value: t('AN01023388211_US'),
                },
            ]);
        } else if (content.defaultValue === '0LOALS1') {
            setOptions([
                {
                    key: '0LOALS1',
                    value: '0LOALS1',
                },
                {
                    key: 'Others',
                    value: t('OTHERS'),
                },
            ]);
        } else if (content.defaultValue === 'SourcingProject') {
            setOptionsSku();
        }

        setCardData(content);
    }, [content.title]);

    const setOptionsSku = () => {
        const optionsList = content?.controlType?.controlTypeOptions?.map(option => ({
            key: option.value,
            value: option.translationValueKey || option.value,
        }));
        setOptions(optionsList);
    };

    const onChange = (value: any) => {
        const configurationValue = value.key === WAVED_APPROACH_OTHER_KEY ? WAVED_APPROACH_OTHER_KEY + otherInputValue : value.key;
        dispatch(
            actions.setCurrentProductConfigurationValue({
                productRunId: productList.currentRunId,
                taskId: content.id,
                configurationKey: content.productConfigurationKey,
                configurationValue: configurationValue,
                updatedBy: USER_ID,
                personalizationConfiguration: content.personalizationConfiguration,
            })
        );
        dispatch(
            actions.setCurrentProductConfigKeys({
                key: content.productConfigurationKey,
                value: configurationValue,
            })
        );
        setValue(value.key);
        if (value.key !== WAVED_APPROACH_OTHER_KEY) {
            setOtherInputValue('');
        }
    };
    const handleOtherInputChange = (event: any) => {
        const newOthers = event.target.value;
        if (multiSelectValues.values.includes(OTHER_KEY) || multiSelectValues.values.includes(CERTIFICATE_OTHER_KEY)) {
            setMultiSelectValues({
                ...multiSelectValues,
                others: newOthers,
            });

            dispatch(
                actions.setCurrentProductConfigurationValue({
                    productRunId: productList.currentRunId,
                    taskId: content.id,
                    configurationKey: content.productConfigurationKey,
                    configurationValue: JSON.stringify({ values: multiSelectValues.values, others: newOthers }),
                    updatedBy: USER_ID,
                    personalizationConfiguration: content.personalizationConfiguration,
                })
            );
        }
        if (value === WAVED_APPROACH_OTHER_KEY) {
            setOtherInputValue(newOthers);
            dispatch(
                actions.setCurrentProductConfigurationValue({
                    productRunId: productList.currentRunId,
                    taskId: content.id,
                    configurationKey: content.productConfigurationKey,
                    configurationValue: WAVED_APPROACH_OTHER_KEY + newOthers,
                    updatedBy: USER_ID,
                    personalizationConfiguration: content.personalizationConfiguration,
                })
            );
        }
    };

    const onCheckboxChange = (elm: any) => {
        const newValues = [...multiSelectValues.values];
        if (newValues.includes(elm.key)) {
            const index = newValues.indexOf(elm.key);
            newValues.splice(index, 1);
            if (elm.key === OTHER_KEY) {
                multiSelectValues.others = '';
            }
        } else {
            newValues.push(elm.key);
            if (elm.key === OTHER_KEY) {
                multiSelectValues.others = multiSelectValues.others || '';
            }
        }

        setMultiSelectValues({
            ...multiSelectValues,
            values: newValues,
        });

        dispatch(
            actions.setCurrentProductConfigurationValue({
                productRunId: productList.currentRunId,
                taskId: content.id,
                configurationKey: content.productConfigurationKey,
                configurationValue: JSON.stringify({ values: newValues, others: multiSelectValues.others }),
                updatedBy: USER_ID,
                personalizationConfiguration: content.personalizationConfiguration,
            })
        );
    };
    const handleDropDownChange = (event: any) => {
        const selectedValue = event.detail.selectedOption.dataset.value;
        const selectedOption = options.find((option: any) => option.key === selectedValue);
        if (selectedOption) {
            onChange(selectedOption);
        } else if (selectedValue === '') {
            onChange({ key: '', value: '' });
        }
    };
    const sectionStyle = {
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
    };
    const textStyle = {
        paddingLeft: ThemingParameters.sapContent_Padding_M,
        paddingRight: ThemingParameters.sapContent_Padding_M,
    };

    const controlRenderer = (content: any) => {
        switch (content.controlType?.name) {
            case 'checkbox':
                return getCheckboxControl(content);
            case 'dropdown':
                return getDropdownControl();
            case 'freetext':
                return getFreeTextControl(content);
            case 'radiobox':
                return getRadioButtonControl();
            default:
                return getRadioButtonControl();
        }
    };

    function getCertificatesCheckBoxControlType() {
        const notApplyCertificateOption = options[options.length - 1].key;
        const certificatesNotApply: boolean = multiSelectValues.values.includes(notApplyCertificateOption);

        const handleCertificateChange = (option: { key: string; value: string }) => {
            const isSelected = multiSelectValues.values.includes(option.key);

            let updatedValues: string[] = [];
            let updatedOthers: string;

            if (option.key === notApplyCertificateOption) {
                updatedValues = isSelected ? [] : [notApplyCertificateOption];
                updatedOthers = '';
            } else {
                updatedValues = isSelected
                    ? multiSelectValues.values.filter(value => value !== option.key)
                    : [...multiSelectValues.values, option.key];

                updatedOthers = isSelected && option.key === CERTIFICATE_OTHER_KEY ? '' : multiSelectValues.others;
            }

            setMultiSelectValues({
                values: updatedValues,
                others: updatedOthers,
            });

            dispatch(
                actions.setCurrentProductConfigurationValue({
                    productRunId: productList.currentRunId,
                    taskId: content.id,
                    configurationKey: content.productConfigurationKey,
                    configurationValue: JSON.stringify({ values: updatedValues, others: updatedOthers }),
                    updatedBy: USER_ID,
                    personalizationConfiguration: content.personalizationConfiguration,
                })
            );
        };

        return options.map((option: { key: string; value: string }) => (
            <FlexBox key={option.key} gap="1rem" alignItems="Center">
                <CheckBox
                    checked={multiSelectValues.values.includes(option.key)}
                    text={t({ key: option.value, defaultText: option.key })}
                    onChange={() => handleCertificateChange(option)}
                    wrappingType="Normal"
                    disabled={certificatesNotApply && option.key !== notApplyCertificateOption}
                />
                {multiSelectValues.values.includes(CERTIFICATE_OTHER_KEY) && option.value === CERTIFICATE_OTHER_KEY && (
                    <Input value={multiSelectValues.others} placeholder={t('OTHER_PLEASE_SPECIFY')} onChange={handleOtherInputChange} />
                )}
            </FlexBox>
        ));
    }
    const getCheckboxControl = (content: any) => {
        if (content.step?.name === 'Certificate Types') {
            return getCertificatesCheckBoxControlType();
        }
        return options.map((elm: any) => (
            <div key={elm.key} style={{ display: 'flex', alignItems: 'center' }}>
                <CheckBox
                    checked={multiSelectValues.values.includes(elm.key)}
                    name={cardData.contentTypeId}
                    text={t({ key: elm.value, defaultText: elm.key })}
                    onChange={() => onCheckboxChange(elm)}
                    disabled={productList.readOnlyMode}
                    wrappingType="Normal"
                />
                {elm.key === OTHER_KEY && (
                    <Input value={multiSelectValues.others} placeholder="Other" onChange={handleOtherInputChange} style={{ marginLeft: '10px' }} />
                )}
            </div>
        ));
    };
    const getRadioButtonControl = () => {
        return options.map((elm: any) => (
            <div key={elm.key} style={{ display: 'flex', alignItems: 'center' }}>
                <RadioButton
                    checked={elm.key === value || (elm.key === WAVED_APPROACH_OTHER_KEY && otherInputValue !== '')}
                    name={cardData.contentTypeId}
                    text={t({ key: elm.value, defaultText: elm.key })}
                    onChange={() => onChange(elm)}
                    disabled={productList.readOnlyMode}
                    wrappingType="Normal"
                />
                {elm.key === WAVED_APPROACH_OTHER_KEY && (
                    <Input value={otherInputValue} placeholder="Other" onChange={handleOtherInputChange} style={{ marginLeft: '10px' }} />
                )}
            </div>
        ));
    };

    const getDropdownControl = () => {
        return (
            <Select value={value} style={{ minWidth: '300px', width: '100%' }} onLiveChange={handleDropDownChange}>
                <Option value="" data-value="">
                    {t('SELECT')}
                </Option>
                {options.map((option: any) => (
                    <Option key={option.key} value={option.key} data-value={option.key} selected={option.key === value}>
                        {t({ key: option.value, defaultText: option.key })}
                    </Option>
                ))}
            </Select>
        );
    };

    const handleTextInputChange = (event: Ui5CustomEvent<InputDomRef, never>) => {
        const value = event.target.value;
        onChange({ key: value, value: value });
    };

    const getFreeTextControl = (content: any) => {
        const text = content.personalizationConfiguration?.configurationValue;
        const placeHolderMapper: { [key: string]: string } = {
            Hostname: 'Hostname',
            'Icertis System': 'Idp',
        };
        return (
            <Input
                value={text}
                key={content.step?.name}
                placeholder={placeHolderMapper[content.step?.name]}
                onChange={handleTextInputChange}
                required
                disabled={productList.readOnlyMode}
                maxlength={255}
            />
        );
    };
    return (
        <>
            {cardData && (
                <div className="content__datacontroller">
                    <section style={sectionStyle}>
                        <TaskIcon icon={src} />
                        <TaskTitle />
                        <Text style={textStyle}>{t({ key: cardData.translationTitleKey, defaultText: cardData.title })}</Text>
                        <FlexBox direction="Column" style={{ padding: '1rem' }}>
                            {controlRenderer(content)}
                        </FlexBox>
                    </section>
                </div>
            )}
        </>
    );
};

export default DbLayout;
